import React from 'react';

import useAutoBounceScroll from '../../hooks/useAutoBounceScroll';
import { buildZmanimDisplayModel } from '../../utils/zmanim';

function ZmanIcon({ name }) {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    'data-zman-icon': name
  };

  switch (name) {
    case 'book':
      return (
        <svg {...commonProps}>
          <path d="M3.5 5.5c2.8-.9 5.4-.45 8.5 1.35v12.1c-3.1-1.8-5.7-2.25-8.5-1.35z" />
          <path d="M20.5 5.5c-2.8-.9-5.4-.45-8.5 1.35v12.1c3.1-1.8 5.7-2.25 8.5-1.35z" />
          <path d="M12 6.85v12.1" />
        </svg>
      );
    case 'sun':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" />
        </svg>
      );
    case 'sunset':
      return (
        <svg {...commonProps} fill="currentColor" stroke="none">
          <path d="M3,12H7A5,5 0 0,1 12,7A5,5 0 0,1 17,12H21A1,1 0 0,1 22,13A1,1 0 0,1 21,14H3A1,1 0 0,1 2,13A1,1 0 0,1 3,12M15,12A3,3 0 0,0 12,9A3,3 0 0,0 9,12H15M12,2L14.39,5.42C13.65,5.15 12.84,5 12,5C11.16,5 10.35,5.15 9.61,5.42L12,2M3.34,7L7.5,6.65C6.9,7.16 6.36,7.78 5.94,8.5C5.5,9.24 5.25,10 5.11,10.79L3.34,7M20.65,7L18.88,10.79C18.74,10 18.47,9.23 18.05,8.5C17.63,7.78 17.1,7.15 16.5,6.64L20.65,7M12.71,20.71L15.82,17.6C16.21,17.21 16.21,16.57 15.82,16.18C15.43,15.79 14.8,15.79 14.41,16.18L12,18.59L9.59,16.18C9.2,15.79 8.57,15.79 8.18,16.18C7.79,16.57 7.79,17.21 8.18,17.6L11.29,20.71C11.5,20.9 11.74,21 12,21C12.26,21 12.5,20.9 12.71,20.71Z" />
        </svg>
      );
    case 'night':
      return (
        <svg {...commonProps}>
          <path d="M19.5 15.2A8 8 0 0 1 8.8 4.5a8 8 0 1 0 10.7 10.7Z" />
          <path d="m17.5 4 .45 1.25L19.2 5.7l-1.25.45-.45 1.25-.45-1.25-1.25-.45 1.25-.45z" />
          <path d="m20 9 .25.7.7.25-.7.25-.25.7-.25-.7-.7-.25.7-.25z" />
        </svg>
      );
    case 'candles':
      return (
        <svg {...commonProps} data-icon-detail="two-identical-candles">
          <path d="M4 20h16" />
          <g transform="translate(5 0)">
            <path d="M2 3.5c1.5 1.6 1.6 3.3 0 5-1.6-1.7-1.5-3.4 0-5Z" />
            <rect x="0" y="10" width="4" height="9" rx="0.5" />
          </g>
          <g transform="translate(13 0)">
            <path d="M2 3.5c1.5 1.6 1.6 3.3 0 5-1.6-1.7-1.5-3.4 0-5Z" />
            <rect x="0" y="10" width="4" height="9" rx="0.5" />
          </g>
        </svg>
      );
    case 'havdalah':
      return (
        <svg {...commonProps} data-icon-detail="moon-three-stars">
          <path d="M15.8 16.8A7 7 0 0 1 7.2 7.1 7 7 0 1 0 15.8 16.8Z" />
          <path d="m16.8 4 .45 1.2 1.2.45-1.2.45-.45 1.2-.45-1.2-1.2-.45 1.2-.45z" />
          <path d="m20 9 .3.8.8.3-.8.3-.3.8-.3-.8-.8-.3.8-.3z" />
          <path d="m17.5 12.8.25.65.65.25-.65.25-.25.65-.25-.65-.65-.25.65-.25z" />
        </svg>
      );
    case 'hourglass':
      return (
        <svg {...commonProps}>
          <path d="M7 3h10M7 21h10M8 3c0 4 1.5 6 4 8 2.5-2 4-4 4-8M8 21c0-4 1.5-6 4-8 2.5 2 4 4 4 8" />
        </svg>
      );
    case 'coffee':
      return (
        <svg {...commonProps}>
          <path d="M5 9h11v5.5A4.5 4.5 0 0 1 11.5 19h-2A4.5 4.5 0 0 1 5 14.5Z" />
          <path d="M16 11h1.5a2.5 2.5 0 0 1 0 5H16M4 21h14" />
          <path d="M8 3.5c-1 1-1 2.1 0 3.1M12 3c-1 1.1-1 2.2 0 3.3" />
        </svg>
      );
    case 'sunrise':
    default:
      return (
        <svg {...commonProps} fill="currentColor" stroke="none">
          <path d="M3,12H7A5,5 0 0,1 12,7A5,5 0 0,1 17,12H21A1,1 0 0,1 22,13A1,1 0 0,1 21,14H3A1,1 0 0,1 2,13A1,1 0 0,1 3,12M15,12A3,3 0 0,0 12,9A3,3 0 0,0 9,12H15M12,2L14.39,5.42C13.65,5.15 12.84,5 12,5C11.16,5 10.35,5.15 9.61,5.42L12,2M3.34,7L7.5,6.65C6.9,7.16 6.36,7.78 5.94,8.5C5.5,9.24 5.25,10 5.11,10.79L3.34,7M20.65,7L18.88,10.79C18.74,10 18.47,9.23 18.05,8.5C17.63,7.78 17.1,7.15 16.5,6.64L20.65,7M12.71,16.3L15.82,19.41C16.21,19.8 16.21,20.43 15.82,20.82C15.43,21.21 14.8,21.21 14.41,20.82L12,18.41L9.59,20.82C9.2,21.21 8.57,21.21 8.18,20.82C7.79,20.43 7.79,19.8 8.18,19.41L11.29,16.3C11.5,16.1 11.74,16 12,16C12.26,16 12.5,16.1 12.71,16.3Z" />
        </svg>
      );
  }
}

function ZmanCard({ item }) {
  const contextText = item.contextLabel ? `, ${item.contextLabel}` : '';

  return (
    <article
      className={`detail panel-item zman-card zman-card--${item.index}`}
      aria-label={`${item.label}, ${item.time}${contextText}`}
    >
      <span className="zman-card-icon">
        <ZmanIcon name={item.icon} />
      </span>
      <span className="zman-card-label">{item.label}</span>
      <time className="zman-card-time">{item.time}</time>
      {item.contextLabel ? <span className="zman-card-context">{item.contextLabel}</span> : null}
    </article>
  );
}

function ZmanimPanel({ data, loading, now }) {
  const model = React.useMemo(
    () => buildZmanimDisplayModel(data, now),
    [data, now]
  );
  const signature = React.useMemo(
    () =>
      model.pairs
        .flatMap((pair) => pair.items)
        .map((item) => `${item.key}-${item.time}-${item.label}`)
        .join('|'),
    [model.pairs]
  );
  const itemCount = model.pairs.reduce((count, pair) => count + pair.items.length, 0);
  const hasHolyPair = model.pairs.some((pair) => pair.kind === 'holy');
  const panelTitle = model.fast?.isPreview
    ? hasHolyPair
      ? 'זמני היום ומחר'
      : 'זמני מחר'
    : 'זמני היום';
  const { containerRef, resetScroll, hasOverflow } = useAutoBounceScroll({
    enabled: itemCount > 0,
    pageStepRatio: 0.92,
    durationDownMs: 1800,
    durationUpMs: 900,
    pauseBottomMs: 9000,
    pauseTopMs: 9000
  });

  React.useEffect(() => {
    resetScroll();
  }, [resetScroll, signature]);

  return (
    <section className={`glass container panel-fit zmanim-panel zmanim-panel--${model.pairs.length}-pairs`}>
      <h1>{panelTitle}</h1>
      {itemCount ? (
        <div
          ref={containerRef}
          className={`panel-content zmanim-grid ${hasOverflow ? 'panel-content-scroll' : ''}`}
        >
          {model.pairs.map((pair) => (
            <div key={pair.key} className={`zmanim-pair zmanim-pair--${pair.kind}`}>
              {pair.items.map((item) => <ZmanCard key={item.key} item={item} />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="panel-content panel-placeholder">
          {loading ? 'טוען מידע...' : 'אין מידע להצגה'}
        </div>
      )}
    </section>
  );
}

export default React.memo(ZmanimPanel);
